import { ProfileSection } from './ProfileSection';
import type { MarketingDraft } from './marketingDraft';

// 37-value canonical amenity list in 4 labeled groups (ADR-LS-007)
const AMENITY_GROUPS: { key: string; groupLabel: string; items: { value: string; label: string }[] }[] = [
  {
    key: 'features',
    groupLabel: 'Features',
    items: [
      { value: 'pool', label: 'Pool' },
      { value: 'fitness_center', label: 'Fitness center' },
      { value: 'elevator', label: 'Elevator' },
      { value: 'doorman', label: 'Doorman' },
      { value: 'rooftop', label: 'Rooftop deck' },
      { value: 'storage', label: 'Storage' },
      { value: 'bike_storage', label: 'Bike storage' },
      { value: 'balcony', label: 'Balcony' },
      { value: 'patio', label: 'Patio' },
      { value: 'yard', label: 'Yard' },
      { value: 'fireplace', label: 'Fireplace' },
      { value: 'dishwasher', label: 'Dishwasher' },
      { value: 'hardwood_floors', label: 'Hardwood floors' },
      { value: 'high_ceilings', label: 'High ceilings' },
      { value: 'ev_charging', label: 'EV charging' },
    ],
  },
  {
    key: 'appliances',
    groupLabel: 'Appliances',
    items: [
      { value: 'washer', label: 'Washer' },
      { value: 'dryer', label: 'Dryer' },
      { value: 'refrigerator', label: 'Refrigerator' },
      { value: 'microwave', label: 'Microwave' },
      { value: 'garbage_disposal', label: 'Garbage disposal' },
      { value: 'range_oven', label: 'Range / oven' },
      { value: 'freezer', label: 'Freezer' },
      { value: 'large_closets', label: 'Large closets' },
      { value: 'ceiling_fan', label: 'Ceiling fan' },
      { value: 'window_coverings', label: 'Window coverings' },
    ],
  },
  {
    key: 'security',
    groupLabel: 'Security and access',
    items: [
      { value: 'gated_entry', label: 'Gated entry' },
      { value: 'controlled_access', label: 'Controlled access' },
      { value: 'security_system', label: 'Security system' },
      { value: 'intercom', label: 'Intercom' },
      { value: 'package_receiving', label: 'Package receiving' },
    ],
  },
  {
    key: 'recreation',
    groupLabel: 'Recreation and outdoor',
    items: [
      { value: 'hot_tub_spa', label: 'Hot tub / spa' },
      { value: 'sauna', label: 'Sauna' },
      { value: 'barbecue_area', label: 'Barbecue area' },
      { value: 'courtyard', label: 'Courtyard' },
      { value: 'tennis_court', label: 'Tennis court' },
      { value: 'business_center', label: 'Business center' },
      { value: 'concierge', label: 'Concierge' },
    ],
  },
];

interface Props {
  draft: MarketingDraft;
  onChange: (patch: Partial<MarketingDraft>) => void;
}

export function ProfileSectionAmenities({ draft, onChange }: Props) {
  const selected = new Set(draft.amenities);
  // Only values the page can show: a legacy or unknown stored value is not a pressed chip, so it is not counted.
  const selectedCount = AMENITY_GROUPS.reduce((n, g) => n + g.items.filter((i) => selected.has(i.value)).length, 0);

  const toggle = (value: string) =>
    onChange({ amenities: selected.has(value) ? draft.amenities.filter((v) => v !== value) : [...draft.amenities, value] });

  return (
    <ProfileSection
      title="Amenities"
      testId="marketing-details-amenities"
      aside={selectedCount > 0 ? `${selectedCount} selected` : undefined}
    >
      <p className="mb-3 text-xs text-muted-foreground">Select all amenities available at or in this unit.</p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
        {AMENITY_GROUPS.map((group) => (
          <div key={group.key} role="group" aria-labelledby={`amenity-group-${group.key}-heading`}>
            <h3 id={`amenity-group-${group.key}-heading`} className="mb-2 text-xs font-semibold text-muted-foreground">
              {group.groupLabel}
            </h3>
            <div className="flex flex-wrap gap-2">
              {group.items.map((amenity) => {
                const pressed = selected.has(amenity.value);
                return (
                  <span key={amenity.value} data-testid={`amenity-checkbox-${amenity.value}`}>
                    <button
                      type="button"
                      aria-pressed={pressed}
                      onClick={() => toggle(amenity.value)}
                      data-testid={`amenity-${amenity.value}`}
                      className={
                        pressed
                          ? 'min-h-[44px] rounded-full border border-primary bg-primary/15 px-4 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                          : 'min-h-[44px] rounded-full border border-input bg-background px-4 text-sm text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                      }
                    >
                      {amenity.label}
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </ProfileSection>
  );
}
